// Split out of InstanceDetailPage.tsx, then migrated onto components/ui.
//
// The charts themselves are untouched. Recharts writes fill/stroke as SVG
// presentation attributes, which do not resolve var(), so every axis and grid
// carries a `className` next to its hex value — that pair is load-bearing.
// The migration surface here is the panels and the range control around them.

import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '../../store/authStore'
import { infrastructureApi } from '../../api/client'
import type { Instance, RangeKey } from '../../api/client'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as ReTooltip, ResponsiveContainer as ReContainer, Legend as ReLegend,
} from 'recharts'
import { Card, CardHeader, EmptyState, SegmentedControl, Skeleton } from '../../components/ui'

const RANGES: { value: RangeKey; label: string }[] = [
  { value: '24h', label: '24h' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
]

export function ResourcesTab({ instance }: { instance: Instance }) {
  const { t } = useTranslation()
  const role = useAuthStore((s) => s.role)
  const [range, setRange] = useState<RangeKey>('24h')

  const { data, isLoading } = useQuery({
    queryKey: ['instance-resources', instance.id, range],
    queryFn: () => infrastructureApi.instanceTimeseries(instance.id, range),
    enabled: role === 'admin',
    staleTime: 30_000,
    refetchInterval: range === '24h' ? 60_000 : false,
  })

  const fmtBytes = (n: number): string => {
    if (!n) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.min(sizes.length - 1, Math.floor(Math.log(n) / Math.log(k)))
    return `${(n / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`
  }

  const odooSeries = useMemo(() => {
    if (!data) return []
    return data.series.filter((p) => p.container_role === 'odoo').map((p) => ({
      t: new Date(p.hour_bucket).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit' }),
      cpu: p.cpu_percent_avg,
      mem_mb: p.mem_bytes_avg / (1024 * 1024),
      net_mb: (p.net_rx_delta_bytes + p.net_tx_delta_bytes) / (1024 * 1024),
      disk_mb: (p.blk_read_delta_bytes + p.blk_write_delta_bytes) / (1024 * 1024),
    }))
  }, [data])

  const pgSeries = useMemo(() => {
    if (!data) return []
    return data.series.filter((p) => p.container_role === 'pg').map((p) => ({
      t: new Date(p.hour_bucket).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit' }),
      cpu: p.cpu_percent_avg,
      mem_mb: p.mem_bytes_avg / (1024 * 1024),
    }))
  }, [data])

  const storageSeries = useMemo(() => {
    if (!data) return []
    return data.volumes.map((v) => ({
      day: v.day.slice(5, 10),
      db_mb: v.db_bytes / (1024 * 1024),
      filestore_mb: v.filestore_bytes / (1024 * 1024),
      addons_mb: v.addons_bytes / (1024 * 1024),
    }))
  }, [data])

  if (role !== 'admin') {
    return (
      <Card>
        <p className="text-sm text-gray-500">{t('detail.resources.adminOnly')}</p>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-800">{t('detail.resources.title')}</h3>
        <SegmentedControl
          size="sm"
          value={range}
          onChange={setRange}
          options={RANGES}
          label={t('detail.resources.rangeLabel', { defaultValue: 'Date range' })}
        />
      </div>

      <Card>
        <CardHeader className="mb-3" title={t('detail.resources.odooCpuMem')} />
        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : odooSeries.length > 0 ? (
          <ReContainer width="100%" height={240}>
            <LineChart data={odooSeries} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" className="stroke-gray-200" />
              <XAxis dataKey="t" tick={{ fontSize: 10, fill: '#6b7280', className: 'fill-gray-500' }} />
              <YAxis yAxisId="cpu" tick={{ fontSize: 10, fill: '#6b7280', className: 'fill-gray-500' }} domain={[0, 'auto']} />
              <YAxis yAxisId="mem" orientation="right" tick={{ fontSize: 10, fill: '#6b7280', className: 'fill-gray-500' }} />
              <ReTooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
              <ReLegend wrapperStyle={{ fontSize: 11 }} />
              <Line yAxisId="cpu" type="monotone" dataKey="cpu" name={`CPU %`} stroke="#0ea5e9" strokeWidth={2} dot={false} />
              <Line yAxisId="mem" type="monotone" dataKey="mem_mb" name={`RAM (MB)`} stroke="#f59e0b" strokeWidth={2} dot={false} />
            </LineChart>
          </ReContainer>
        ) : (
          <EmptyState className="py-8" title={t('detail.resources.noData')} />
        )}
      </Card>

      <Card>
        <CardHeader className="mb-3" title={t('detail.resources.odooNetIO')} />
        {odooSeries.length > 0 ? (
          <ReContainer width="100%" height={200}>
            <LineChart data={odooSeries} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" className="stroke-gray-200" />
              <XAxis dataKey="t" tick={{ fontSize: 10, fill: '#6b7280', className: 'fill-gray-500' }} />
              <YAxis tick={{ fontSize: 10, fill: '#6b7280', className: 'fill-gray-500' }} />
              <ReTooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} formatter={(v) => `${Number(v).toFixed(1)} MB`} />
              <ReLegend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="net_mb" name={t('detail.resources.network')} stroke="#10b981" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="disk_mb" name={t('detail.resources.diskIO')} stroke="#8b5cf6" strokeWidth={2} dot={false} />
            </LineChart>
          </ReContainer>
        ) : (
          <EmptyState className="py-8" title={t('detail.resources.noData')} />
        )}
      </Card>

      <Card>
        <CardHeader className="mb-3" title={t('detail.resources.pgCpuMem')} />
        {pgSeries.length > 0 ? (
          <ReContainer width="100%" height={200}>
            <LineChart data={pgSeries} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" className="stroke-gray-200" />
              <XAxis dataKey="t" tick={{ fontSize: 10, fill: '#6b7280', className: 'fill-gray-500' }} />
              <YAxis yAxisId="cpu" tick={{ fontSize: 10, fill: '#6b7280', className: 'fill-gray-500' }} />
              <YAxis yAxisId="mem" orientation="right" tick={{ fontSize: 10, fill: '#6b7280', className: 'fill-gray-500' }} />
              <ReTooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
              <ReLegend wrapperStyle={{ fontSize: 11 }} />
              <Line yAxisId="cpu" type="monotone" dataKey="cpu" name={`CPU %`} stroke="#0ea5e9" strokeWidth={2} dot={false} />
              <Line yAxisId="mem" type="monotone" dataKey="mem_mb" name={`RAM (MB)`} stroke="#f59e0b" strokeWidth={2} dot={false} />
            </LineChart>
          </ReContainer>
        ) : (
          <EmptyState className="py-8" title={t('detail.resources.noData')} />
        )}
      </Card>

      <Card>
        <CardHeader className="mb-3" title={t('detail.resources.storageTrend')} />
        {storageSeries.length > 0 ? (
          <ReContainer width="100%" height={200}>
            <LineChart data={storageSeries} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" className="stroke-gray-200" />
              <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#6b7280', className: 'fill-gray-500' }} />
              <YAxis tick={{ fontSize: 10, fill: '#6b7280', className: 'fill-gray-500' }} />
              <ReTooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} formatter={(v) => `${Number(v).toFixed(1)} MB`} />
              <ReLegend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="db_mb" name={`DB`} stroke="#0ea5e9" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="filestore_mb" name={`Filestore`} stroke="#10b981" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="addons_mb" name={`Addons`} stroke="#f59e0b" strokeWidth={2} dot={false} />
            </LineChart>
          </ReContainer>
        ) : (
          <EmptyState className="py-8" title={t('detail.resources.noStorageData')} />
        )}
        {data && data.volumes.length > 0 && (
          <div className="mt-4 text-xs text-gray-500">
            {t('detail.resources.lastMeasured')}: {new Date(data.volumes[data.volumes.length - 1].measured_at).toLocaleString()}
            {' · '}
            {t('detail.resources.totalStorage')}: {fmtBytes(
              data.volumes[data.volumes.length - 1].db_bytes +
              data.volumes[data.volumes.length - 1].filestore_bytes +
              data.volumes[data.volumes.length - 1].addons_bytes
            )}
          </div>
        )}
      </Card>
    </div>
  )
}
